import { create } from 'zustand'
import type { Role, UserProfile } from '@/types'
import {
  observeAuth,
  login as loginSvc,
  logout as logoutSvc,
  registerUser,
} from '@/services/auth.service'
import { isFirebaseConfigured } from '@/lib/firebase'
import { markJustSignedUp } from '@/lib/onboarding'
import { usePrefsStore } from '@/store/prefsStore'

type Status = 'initializing' | 'ready'

interface AuthState {
  status: Status
  profile: UserProfile | null
  initialized: boolean
  init: () => void
  login: (username: string, password: string) => Promise<UserProfile>
  signup: (input: {
    username: string
    password: string
    displayName: string
    role?: Role
  }) => Promise<UserProfile>
  logout: () => Promise<void>
  setProfile: (p: UserProfile | null) => void
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: 'initializing',
  profile: null,
  initialized: false,

  init: () => {
    if (get().initialized) return
    set({ initialized: true })
    if (!isFirebaseConfigured) {
      set({ status: 'ready', profile: null })
      return
    }
    observeAuth((profile) => {
      set({ profile, status: 'ready' })
      // Pull/seed this user's cross-device preferences (or stop syncing on sign-out).
      void usePrefsStore.getState().syncUser(profile?.id ?? null)
    })
  },

  login: async (username, password) => {
    const profile = await loginSvc(username, password)
    set({ profile, status: 'ready' })
    return profile
  },

  signup: async (input) => {
    const profile = await registerUser(input)
    markJustSignedUp()
    set({ profile, status: 'ready' })
    return profile
  },

  logout: async () => {
    // Clear the local session even if the network sign-out call fails — a user
    // who asked to leave must not be left in an authenticated client state.
    // (`observeAuth` also nulls `profile` on a successful sign-out; this covers
    // the throwing path.)
    try {
      await logoutSvc()
    } finally {
      set({ profile: null })
    }
  },

  setProfile: (p) => set({ profile: p }),
}))

/* -------------------- Role / permission helpers -------------------- */

export function hasRole(profile: UserProfile | null, ...roles: Role[]): boolean {
  if (!profile) return false
  // The master admin has full access — it satisfies every role requirement.
  if (profile.role === 'MASTER_ADMIN') return true
  return roles.includes(profile.role)
}

/** The single top-level account. Full permissions, manages users. */
export const isMasterAdmin = (p: UserProfile | null) =>
  hasRole(p, 'MASTER_ADMIN')

/** Master admin or a normal admin. */
export const isAdmin = (p: UserProfile | null) =>
  hasRole(p, 'MASTER_ADMIN', 'ADMIN')

export const canManage = (p: UserProfile | null) =>
  hasRole(p, 'MASTER_ADMIN', 'ADMIN', 'TEAM_MANAGER', 'TOURNAMENT_MANAGER')

/** Whether this role can create and score matches. Mirrors `firestore.rules`' `canScore()`. A
 *  TEAM_MANAGER can score too: one account holds exactly one role, so "Team Manager and Scorer" is
 *  expressed by Team Manager including scoring (as Admin and Tournament Manager already do) —
 *  promoting someone to Team Manager must not take scoring away. */
export const canScore = (p: UserProfile | null) =>
  hasRole(p, 'MASTER_ADMIN', 'ADMIN', 'SCORER', 'TEAM_MANAGER', 'TOURNAMENT_MANAGER')

export const canManagePlayers = (p: UserProfile | null) =>
  hasRole(p, 'MASTER_ADMIN', 'ADMIN', 'TEAM_MANAGER')

/**
 * Whether this profile can create a player/team under their own ownership — mirrors
 * `firestore.rules`' `canBuildRoster()` exactly. Narrower than granting SCORER full
 * `canManagePlayers`/`canManage` (clubs/seasons and editing/deleting someone else's
 * row stay off-limits) but wide enough that a fresh self-signup (always SCORER) can
 * actually build the roster a new match needs, not just watch.
 */
export const canBuildRoster = (p: UserProfile | null) =>
  hasRole(p, 'MASTER_ADMIN', 'ADMIN', 'TEAM_MANAGER', 'TOURNAMENT_MANAGER', 'SCORER')

export const canManageTournaments = (p: UserProfile | null) =>
  hasRole(p, 'MASTER_ADMIN', 'ADMIN', 'TOURNAMENT_MANAGER')

/**
 * Whether this profile can *create* a new tournament — narrower than
 * `canManageTournaments`. Mirrors `firestore.rules`' `canCreateTournament()`
 * exactly: the master admin bypasses everything, otherwise ADMIN/TOURNAMENT_MANAGER,
 * OR a server-verified Tournament Admin (`tournamentAdmin === true` — set only by the
 * media Worker's service account after email-OTP verification, never client-settable;
 * there is no master-approval step) — but only for the two roles that can't create a
 * tournament on their role alone (SCORER, TEAM_MANAGER), and never for a banned account.
 * A VIEWER holding the flag gets nothing. This is a UX convenience only; the rule is the real gate.
 */
export const canCreateTournament = (p: UserProfile | null) =>
  isMasterAdmin(p) ||
  hasRole(p, 'ADMIN', 'TOURNAMENT_MANAGER') ||
  (p?.status !== 'banned' && hasRole(p, 'SCORER', 'TEAM_MANAGER') && p?.tournamentAdmin === true)

/**
 * Owner-scoping for "fully isolated" admins: the master admin sees everything;
 * a normal admin only sees rows they own. Returns the uid to filter by, or
 * `null` to mean "no filter" (see all).
 */
export function ownerScope(p: UserProfile | null): string | null {
  if (!p) return null
  if (p.role === 'MASTER_ADMIN') return null
  return p.id
}

/** Can this profile manage (edit/delete) an owned row? */
export function ownsOrMaster(
  p: UserProfile | null,
  ownerId?: string | null,
): boolean {
  if (!p) return false
  if (p.role === 'MASTER_ADMIN') return true
  return !!ownerId && ownerId === p.id
}

/**
 * Can this profile manage THIS specific match — score it, edit it, upload/delete its
 * video or photos? Mirrors `firestore.rules`' `isOwnerMasterOrDelegatedScorer()` exactly:
 * the match's own owner (any role), the master admin, or its assigned `scorerId` (only if
 * their current role can still score) — nobody else, regardless of role.
 *
 * Deliberately narrower than `canScore(profile)`, which only checks the profile's role and
 * says nothing about THIS match — using that alone let any TOURNAMENT_MANAGER/SCORER/ADMIN
 * see match-management controls (Score live, Start scoring, Edit, Upload video, Manage
 * gallery) on a match they don't own or aren't assigned to. The server-side gates
 * (`firestore.rules`, the media Worker's `canWriteFolder`) already enforced this correctly
 * (or now do, for the Worker) — this brings the UI's own gate in line so those controls
 * don't appear only to fail when used.
 */
export function isOwnerOrDelegatedScorer(
  p: UserProfile | null,
  match: { ownerId?: string | null; scorerId?: string | null } | null | undefined,
): boolean {
  if (!p || !match) return false
  if (p.role === 'MASTER_ADMIN') return true
  if (match.ownerId === p.id) return true
  return canScore(p) && !!match.scorerId && match.scorerId === p.id
}
